/**
 * UC2 — the relational split: Aurora PostgreSQL for rules, DynamoDB for the issued-serial ledger.
 *
 * THE POINT OF THIS PANEL IS DISCRIMINATION, NOT OUTPUT.
 * UC2 only justifies a relational store if rule resolution genuinely needs one — a real SQL join
 * across customer → service → rule → stc. A UI that shows only the winning rule proves nothing:
 * one row is indistinguishable from a hardcoded constant. So the rules Table renders the LOSING
 * CANDIDATES TOO, each with its `selected` flag, the tie-breakers that decided it
 * (customer-specific, ZIP prefix), and the construct it would have chosen. Seeing four candidates
 * narrow to one is the evidence; seeing the winner alone is an assertion.
 *
 * `services/rules-api` returns those candidates additively (its README documents the
 * `selected: true/false` field); the pinned contract shape in docs/api-contract.md shows only
 * `{ruleId, name, outcome}`, so this panel treats every extra column as optional and degrades
 * gracefully rather than breaking against a stricter server.
 */

import { useState } from 'react';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Form from '@cloudscape-design/components/form';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Select from '@cloudscape-design/components/select';
import Button from '@cloudscape-design/components/button';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Table from '@cloudscape-design/components/table';
import Badge from '@cloudscape-design/components/badge';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import KeyValuePairs from '@cloudscape-design/components/key-value-pairs';
import SerialCard from './SerialCard.jsx';
import RawJson from './RawJson.jsx';
import { Impb, barcodeFromApiItem, ambiguousWith } from '../lib/impb.js';
import { timedFetch, errorText, isPlaceholder } from '../lib/api.js';
import {
  CUSTOMER_OPTIONS,
  SERVICE_OPTIONS,
  DEFAULT_CUSTOMER,
  DEFAULT_SERVICE,
  ZIP_DESCRIPTION,
  zipProblem,
  parseWeightOz,
  bandForOz,
} from '../lib/rules-catalog.js';

/*
 * THE CUSTOMER AND SERVICE OPTIONS COME FROM `lib/rules-catalog.js`, SHARED WITH UC4.
 *
 * That sharing is what makes the two engines comparable at all: the schemas are deliberately
 * identical, so the only variable between UC2 and UC4 is the database. Two hand-maintained forms
 * would quietly reintroduce a second variable.
 *
 * Both are SELECTs over the actually-seeded ids rather than free text. A control offering a value
 * the engine cannot resolve is worse than a missing one — it manufactures evidence against the
 * thing it exists to demonstrate, because a viewer reasonably reads the failure as a broken rule
 * engine rather than as a client offering a code that was never seeded.
 */

const LOCAL_MID_9 = '912345678';
const LOCAL_STC = '123';

/**
 * The rules table. `selected` distinguishes the winner from the candidates it beat — which is the
 * whole demonstration.
 *
 * ⚠️ WHEN THE ENDPOINT DOES NOT REPORT RESOLUTION DETAIL, SAY SO ONCE — DO NOT DASH EVERY CELL.
 * A stub or a placeholder returns rule names without the `selected` flag, and a grid of dashes
 * makes the table that exists to prove discrimination prove nothing, while reading as broken rather
 * than as a limitation of the endpoint. So if NO candidate carries `selected`, the table drops the
 * columns it has no data for and shows one banner naming the endpoint as the reason. If SOME do,
 * the flag is authoritative and the winner and losers are badged.
 */
function RulesTable({ rules, illustrative, endpointLabel }) {
  const items = (rules || []).map((r, i) => ({ ...r, key: `${r.ruleId}-${r.name}-${i}` }));
  // "Did this endpoint report resolution at all?" — one decision, not per-cell guessing.
  const reportsResolution = items.some((r) => typeof r.selected === 'boolean');
  const reportsDetail = items.some((r) => r.construct || typeof r.customerSpecific === 'boolean' || r.destZipPrefix);

  const columns = [];
  if (reportsResolution) {
    columns.push({
      id: 'selected',
      header: 'Outcome',
      cell: (r) =>
        r.selected ? <Badge color="green">SELECTED</Badge> : <Badge color="grey">lost</Badge>,
    });
  }
  columns.push(
    { id: 'ruleId', header: 'Rule id', cell: (r) => <code>{String(r.ruleId)}</code> },
    { id: 'name', header: 'Rule', cell: (r) => r.name },
    { id: 'outcome', header: 'Resolves to', cell: (r) => <code>{r.outcome}</code> }
  );
  if (reportsDetail) {
    columns.push(
      { id: 'construct', header: 'Construct', cell: (r) => (r.construct ? <code>{r.construct}</code> : '—') },
      {
        id: 'discriminators',
        header: 'Why it matched (or did not)',
        cell: (r) => {
          const bits = [];
          if (r.customerSpecific === true) bits.push('customer-specific');
          if (r.customerSpecific === false) bits.push('generic');
          if (r.destZipPrefix) bits.push(`ZIP prefix ${r.destZipPrefix}`);
          if (r.illustrative) bits.push('ILLUSTRATIVE — not from SQL');
          return bits.length ? bits.join(' · ') : '—';
        },
      }
    );
  }

  return (
    <SpaceBetween size="s">
      {items.length && !reportsResolution ? (
        <Alert type="info" header="Rule resolution detail not returned by this endpoint" data-testid="uc2-no-resolution">
          {illustrative ? (
            <>
              These are illustrative placeholders computed in the browser, so there is no winner to
              mark — nothing was resolved by SQL.
            </>
          ) : (
            <>
              {endpointLabel ? <code>{endpointLabel}</code> : 'This endpoint'} returned rule names but
              no <code>selected</code> flag, so <strong>which candidate won cannot be shown</strong>.
              The real <code>services/rules-api</code> reports it (along with the construct and the
              customer/ZIP tie-breakers); a stub or the infra placeholder does not. The columns it has
              no data for are omitted rather than filled with dashes.
            </>
          )}
        </Alert>
      ) : null}
      <Table
        variant="embedded"
        data-testid="uc2-rules-table"
        items={items}
        trackBy="key"
        empty={<Box variant="p">No rule candidates returned.</Box>}
        columnDefinitions={columns}
        header={
          <Header
            variant="h3"
            counter={`(${items.length})`}
            description={
              illustrative
                ? 'Illustrative placeholders computed in JavaScript — NOT the output of a rule engine.'
                : reportsResolution
                  ? 'Every candidate the SQL join considered, winner and losers. The losers are the evidence that a join happened.'
                  : 'Candidates as reported. Without a selected flag this list cannot show the join discriminating — see the note above.'
            }
          >
            Rule candidates
          </Header>
        }
      />
    </SpaceBetween>
  );
}

export default function Uc2Panel({ mode, endpoints, live, liveProbe, uc2Metrics, onRefreshUc2Metrics }) {
  const [customer, setCustomer] = useState(DEFAULT_CUSTOMER);
  const [service, setService] = useState(DEFAULT_SERVICE);
  const [weightOz, setWeightOz] = useState('12');
  const [destZip, setDestZip] = useState('20260');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const [lookupPic, setLookupPic] = useState('');
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupError, setLookupError] = useState(null);
  const [lookup, setLookup] = useState(null);

  const localNotice = (
    <Alert type="warning" header="LOCAL RENDER MODE — the relational path was NOT exercised">
      {liveProbe && liveProbe.reason ? <>{liveProbe.reason} </> : null}
      No Aurora PostgreSQL, no SQL join, no DynamoDB ledger. The rule candidates below are
      illustrative placeholders computed in JavaScript to show the UI shape; they are{' '}
      <strong>not</strong> the output of a real rule engine, and no ledger row exists, so a ledger
      lookup will not find this PIC. UC2's entire point is the real join — this mode cannot
      demonstrate it.
    </Alert>
  );

  function illustrativeRules(serviceCode, oz) {
    const band = oz <= 16 ? 'under_1lb' : oz <= 160 ? 'under_10lb' : 'over_10lb';
    return [
      { ruleId: 0, name: `service_permitted (${serviceCode})`, outcome: 'ALLOW', illustrative: true },
      { ruleId: 0, name: `weight_band (${oz}oz)`, outcome: band, illustrative: true },
      { ruleId: 0, name: 'stc_selection', outcome: `STC=${LOCAL_STC}`, illustrative: true },
    ];
  }

  async function submitLabel() {
    setError(null);
    setResult(null);
    setBusy(true);
    try {
      // Validate the ZIP the way the SERVER does — 5 OR 9 digits — and no more strictly. A client
      // rule stricter than the server's does not prevent an error, it prevents a success: C01 needs
      // a 9-digit ZIP+4 and the API refuses to pad one, because padding would fabricate routing
      // data.
      const zipBad = zipProblem(destZip);
      if (zipBad) throw new Error(zipBad);
      const w = parseWeightOz(weightOz);
      if (w.error) throw new Error(w.error);
      const oz = w.oz;

      if (live) {
        const res = await timedFetch(
          `${endpoints.rulesApi}/v2/labels`,
          {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({
              customerId: customer.value,
              serviceCode: service.value,
              destZip: destZip.trim(),
              weightOz: oz,
            }),
          },
          15000
        );
        if (!res.ok) throw new Error(errorText(res));
        const payload = res.json || {};
        let barcode = null;
        let barcodeError = null;
        if (payload.label && payload.label.pic) {
          try {
            barcode = barcodeFromApiItem(payload.label);
            setLookupPic(payload.label.pic);
          } catch (err) {
            barcodeError = err.message;
          }
        }
        setResult({ kind: 'live', ms: res.ms, payload, barcode, barcodeError, placeholder: isPlaceholder(payload) });
        onRefreshUc2Metrics();
      } else {
        // C03 carries the destination ZIP, which is what makes it a LABEL rather than a bare serial.
        // C03 takes a ZIP5 and C01 a ZIP+4, so the construct follows the WIDTH THE USER TYPED: this
        // mode's only legitimate job is showing barcode RENDERING, and it cannot do that for a
        // construct it silently substituted.
        const zip = destZip.trim();
        const b = Impb.buildBarcode({
          construct: zip.length === 9 ? 'C01' : 'C03',
          serviceTypeCode: LOCAL_STC,
          mailerId: LOCAL_MID_9,
          serial: Math.floor(Math.random() * 9999999),
          zip,
        });
        setLookupPic(b.pic);
        setResult({ kind: 'local', barcode: b, rules: illustrativeRules(service.value, oz) });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function showRuleSet() {
    setError(null);
    setResult(null);
    setBusy(true);
    try {
      if (!live) {
        setResult({ kind: 'local', rulesOnly: true, rules: illustrativeRules(service.value, Number(weightOz) || 0) });
        return;
      }
      const res = await timedFetch(
        `${endpoints.rulesApi}/v2/rules?customerId=${encodeURIComponent(customer.value)}`,
        {},
        12000
      );
      if (!res.ok) throw new Error(errorText(res));
      const payload = res.json || {};
      setResult({
        kind: 'live',
        rulesOnly: true,
        ms: res.ms,
        payload,
        placeholder: isPlaceholder(payload),
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function doLookup() {
    setLookupError(null);
    setLookup(null);
    setLookupBusy(true);
    try {
      // Accept the grouped human-readable form pasted straight off a label.
      const pic = lookupPic.replace(/[\s-]/g, '');
      if (!/^[0-9]+$/.test(pic)) throw new Error('PIC must be digits (spaces and dashes are ignored)');

      // Validate locally FIRST. A malformed PIC does not deserve a network call, and the MOD 10
      // check digit makes malformedness decidable client-side with certainty.
      const parsed = Impb.parseBarcode(pic);
      if (!parsed.valid) {
        setLookup({ kind: 'rejected', reason: parsed.reason });
        return;
      }

      // A bare PIC cannot always identify its own construct — C01/C03/C04 share a byte-identical
      // 22-digit signature and differ only in a routing element a bare PIC does not carry. State
      // that instead of presenting an inference as fact.
      const alts = ambiguousWith(parsed.parsed.construct);

      if (!live) {
        setLookup({ kind: 'local-valid', parsed: parsed.parsed, alts });
        return;
      }

      const res = await timedFetch(`${endpoints.rulesApi}/v2/labels/${encodeURIComponent(pic)}`, {}, 12000);
      if (res.status === 404) {
        setLookup({ kind: 'not-found', parsed: parsed.parsed, alts, ms: res.ms });
        return;
      }
      if (!res.ok) throw new Error(errorText(res));
      const payload = res.json || {};
      const record = payload.label && typeof payload.label === 'object' ? payload.label : payload;
      if (!record || !record.pic) {
        setLookup({ kind: 'not-found', parsed: parsed.parsed, alts, ms: res.ms, payload });
        return;
      }
      let barcode = null;
      try {
        barcode = barcodeFromApiItem(record);
      } catch (_e) {
        /* the ledger row may not carry full barcode strings; the field table still shows it */
      }
      setLookup({ kind: 'found', parsed: parsed.parsed, alts, ms: res.ms, payload, record, barcode });
    } catch (err) {
      setLookupError(err.message);
    } finally {
      setLookupBusy(false);
    }
  }

  const rulesForDisplay =
    result && result.kind === 'live'
      ? (result.payload && (result.payload.rulesApplied || result.payload.rules)) || []
      : (result && result.rules) || [];

  return (
    <SpaceBetween size="l">
      <Container
        header={
          <Header
            variant="h2"
            description="Aurora PostgreSQL holds the rules (real FKs and joins); DynamoDB holds the issued-serial ledger. Two stores, because the two datasets have opposite consistency needs (D5)."
            actions={
              <Button iconName="refresh" onClick={onRefreshUc2Metrics}>
                Refresh UC2 metrics
              </Button>
            }
          >
            UC2 — relational rules + ledger
          </Header>
        }
      >
        <SpaceBetween size="m">
          {/*
            The gap ratio is first-class on the UC2 path too, rendered here rather than only under
            UC1: a viewer looking at UC2 must be able to see what the relational path costs in
            SERIAL SPACE and not only in milliseconds.
          */}
          <ColumnLayout columns={3} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Serials allocated (UC2)</Box>
              <Box variant="h1" fontSize="heading-xl" color={typeof uc2Metrics.serialsAllocated === 'number' ? undefined : 'text-status-inactive'}>
                <span data-testid="uc2-m-allocated">
                  {typeof uc2Metrics.serialsAllocated === 'number' ? uc2Metrics.serialsAllocated.toLocaleString() : 'n/a'}
                </span>
              </Box>
              <Box variant="small" color="text-body-secondary">reserved via the DynamoDB high-water mark</Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Serials issued (UC2)</Box>
              <Box variant="h1" fontSize="heading-xl" color={typeof uc2Metrics.serialsIssued === 'number' ? undefined : 'text-status-inactive'}>
                <span data-testid="uc2-m-issued">
                  {typeof uc2Metrics.serialsIssued === 'number' ? uc2Metrics.serialsIssued.toLocaleString() : 'n/a'}
                </span>
              </Box>
              <Box variant="small" color="text-body-secondary">actually written to the ledger</Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Gap ratio (UC2)</Box>
              <Box variant="h1" fontSize="display-l" color={typeof uc2Metrics.gapRatio === 'number' ? 'text-status-warning' : 'text-status-inactive'}>
                <span data-testid="uc2-m-gap">
                  {typeof uc2Metrics.gapRatio === 'number' ? `${(uc2Metrics.gapRatio * 100).toFixed(2)}%` : 'n/a'}
                </span>
              </Box>
              <Box variant="small" color="text-body-secondary">same shared buffer, same accounting</Box>
            </div>
          </ColumnLayout>

          <ExpandableSection headerText="What UC2 adds, and what it costs">
            <Box variant="p">
              UC1 needs no joins — a serial is a number. UC2 exists for the part of the workload that
              is <em>genuinely</em> relational: which service codes a customer may use, which weight
              bands apply, which rule wins. That is a small, read-mostly dataset with real foreign
              keys, so it goes in Aurora PostgreSQL and rule resolution is a real SQL join across
              customer → service → rule → stc.
            </Box>
            <Box variant="p">
              The issued-serial ledger stays in DynamoDB. Two stores, because the two datasets have
              opposite consistency and access needs. The join is not free, and the{' '}
              <code>uc2-relational</code> load-test scenario measures what it costs against the UC1
              baseline rather than hand-waving it.
            </Box>
          </ExpandableSection>
        </SpaceBetween>
      </Container>

      <Container header={<Header variant="h2" description="POST /v2/labels — resolves the rule set, vends a serial, writes the ledger row.">Generate a label through the rules engine</Header>}>
        <form onSubmit={(e) => { e.preventDefault(); submitLabel(); }}>
          <Form
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="primary" loading={busy} onClick={submitLabel} data-testid="uc2-submit">
                  Resolve rules &amp; vend label
                </Button>
                <Button onClick={showRuleSet} data-testid="uc2-ruleset">
                  Show customer rule set
                </Button>
              </SpaceBetween>
            }
            errorText={error}
          >
            {/*
              SAME OPTIONS AS UC4, from lib/rules-catalog.js — see the note at the top of this file.
              And as on UC4: NO CONSTRUCT PICKER. Here the construct is what the SQL join RESOLVES,
              so a dropdown would let a viewer override the engine on trial. Changing the customer is
              the honest way to move it.
            */}
            <ColumnLayout columns={4}>
              <FormField
                label="Customer ID"
                description="joined to its permitted services in Aurora — changing this changes the RESOLVED construct"
              >
                <Select
                  selectedOption={customer}
                  options={CUSTOMER_OPTIONS}
                  onChange={({ detail }) => setCustomer(detail.selectedOption)}
                  data-testid="uc2-customer"
                />
              </FormField>
              <FormField label="Service code" description="must be an ACTIVE subscription for the customer">
                <Select selectedOption={service} options={SERVICE_OPTIONS} onChange={({ detail }) => setService(detail.selectedOption)} data-testid="uc2-service" />
              </FormField>
              <FormField
                label="Weight (oz)"
                description={
                  bandForOz(Number(weightOz))
                    ? `selects the weight band rule — ${bandForOz(Number(weightOz))}`
                    : 'selects the weight band rule'
                }
              >
                <Input type="number" value={weightOz} onChange={({ detail }) => setWeightOz(detail.value)} data-testid="uc2-weight" />
              </FormField>
              <FormField label="Destination ZIP" description={ZIP_DESCRIPTION}>
                <Input value={destZip} onChange={({ detail }) => setDestZip(detail.value)} data-testid="uc2-zip" />
              </FormField>
            </ColumnLayout>
          </Form>
        </form>
      </Container>

      {result ? (
        <SpaceBetween size="l">
          {result.kind === 'live' ? (
            result.placeholder ? (
              <Alert type="warning" header="The endpoint answered, but it is the infra PLACEHOLDER — no rule join ran">
                <code>{endpoints.rulesApi}</code> returned HTTP 200 with <code>placeholder: true</code>,
                meaning <code>services/rules-api</code> is not deployed behind this route yet. No
                Aurora query was executed and no ledger row was written. An empty{' '}
                <code>rulesApplied</code> is <strong>not</strong> a resolved rule set, and this UI
                will not present it as one.
              </Alert>
            ) : (
              <>
                <Alert type="success" header="Live response">
                  Rules resolved by a real SQL join in Aurora PostgreSQL; the issued serial was
                  written to the DynamoDB ledger.
                </Alert>
                <Box>
                  <StatusIndicator type="info">
                    round trip <strong>{result.ms.toFixed(1)} ms</strong> — includes the Aurora rule
                    join. Browser-observed, one sample, <strong>not a p99</strong>, and it{' '}
                    <strong>excludes the Apigee hop</strong>. The distributions and the UC1
                    comparison are on the <strong>Load test</strong> tab.
                  </StatusIndicator>
                </Box>
              </>
            )
          ) : (
            localNotice
          )}

          <Container header={<Header variant="h2">Rule resolution</Header>}>
            <RulesTable
              rules={rulesForDisplay}
              illustrative={result.kind === 'local'}
              endpointLabel={result.kind === 'live' ? endpoints.rulesApi : null}
            />
            {result.payload && result.payload.resolution ? (
              <Box margin={{ top: 'm' }}>
                <KeyValuePairs
                  columns={4}
                  items={Object.entries(result.payload.resolution).map(([k, v]) => ({
                    label: k,
                    value: <code>{String(v)}</code>,
                  }))}
                />
              </Box>
            ) : null}
            {result.payload && result.payload.ledgerId ? (
              <Box margin={{ top: 'm' }}>
                <KeyValuePairs
                  columns={1}
                  items={[{ label: 'Ledger id', value: <code data-testid="uc2-ledger-id">{result.payload.ledgerId}</code> }]}
                />
              </Box>
            ) : null}
          </Container>

          {result.barcode ? (
            <SerialCard barcode={result.barcode} index={null} mode={mode} extra={{ live: result.kind === 'live', requestId: result.payload && result.payload.requestId }} />
          ) : null}
          {result.barcodeError ? (
            <Alert type="error" header="Label failed verification">{result.barcodeError}</Alert>
          ) : null}
          {result.payload ? <RawJson value={result.payload} /> : null}
        </SpaceBetween>
      ) : null}

      {/* ------------------------------------------------------------- ledger lookup */}
      <Container
        header={
          <Header
            variant="h2"
            description="GET /v2/labels/{pic} — recover a previously issued label from the DynamoDB ledger. Spaces are ignored, so the grouped human-readable form can be pasted straight off a label."
          >
            Ledger lookup
          </Header>
        }
      >
        <form onSubmit={(e) => { e.preventDefault(); doLookup(); }}>
          <Form
            actions={
              <Button variant="primary" loading={lookupBusy} onClick={doLookup} data-testid="uc2-lookup-submit">
                Look up PIC
              </Button>
            }
            errorText={lookupError}
          >
            <FormField label="PIC" description="22–34 digits depending on construct; the MOD 10 check digit is verified before any network call">
              <Input
                value={lookupPic}
                onChange={({ detail }) => setLookupPic(detail.value)}
                placeholder="9212 3912 3456 7812 3456 70"
                data-testid="uc2-lookup-pic"
              />
            </FormField>
          </Form>
        </form>

        {lookup ? (
          <Box margin={{ top: 'l' }} data-testid="uc2-lookup-result">
            <SpaceBetween size="m">
              {lookup.kind === 'rejected' ? (
                <Alert type="error" header="Rejected locally before any lookup">
                  {lookup.reason}. The MOD 10 data check digit makes this decidable client-side, so no
                  network call was made.
                </Alert>
              ) : null}

              {lookup.parsed ? (
                <Alert type="success" header="PIC is structurally valid">
                  Construct <code>{lookup.parsed.construct}</code>
                  {lookup.alts.length ? (
                    <>
                      {' '}
                      — <em>
                        indistinguishable from {lookup.alts.map((a) => <code key={a}>{a} </code>)}
                        from the PIC alone. These constructs differ only in a routing element that a
                        bare PIC does not carry, so the construct shown here is an inference, not a
                        fact.
                      </em>
                    </>
                  ) : null}
                  . Serial <code>{lookup.parsed.fields.serial}</code>, MOD 10 check digit verified by{' '}
                  <code>shared/impb</code>.
                </Alert>
              ) : null}

              {lookup.kind === 'local-valid' ? (
                <Alert type="warning" header="Ledger NOT consulted">
                  No backend reachable, so this proves only that the PIC is well-formed — not that it
                  was ever issued. Structural validity and ledger existence are different claims.
                </Alert>
              ) : null}

              {lookup.kind === 'not-found' ? (
                <Alert type="error" header="Not in the ledger (404)">
                  The PIC is structurally valid but this deployment has no record of issuing it.
                </Alert>
              ) : null}

              {lookup.kind === 'found' ? (
                <>
                  <StatusIndicator type="success">
                    ledger lookup <strong>{lookup.ms.toFixed(1)} ms</strong> — record found
                  </StatusIndicator>
                  <KeyValuePairs
                    columns={3}
                    items={Object.entries(lookup.record)
                      .filter(([, v]) => v !== null && typeof v !== 'object')
                      .map(([k, v]) => ({ label: k, value: <code>{String(v)}</code> }))}
                  />
                  {lookup.barcode ? (
                    <SerialCard barcode={lookup.barcode} index={null} mode={mode} extra={{ live: true }} />
                  ) : null}
                </>
              ) : null}

              {lookup.payload ? <RawJson value={lookup.payload} /> : null}
            </SpaceBetween>
          </Box>
        ) : null}
      </Container>
    </SpaceBetween>
  );
}
